import { Injectable, EventEmitter } from '@angular/core';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { Subject, Subscription, Observable, timer } from 'rxjs';
import { Router } from '@angular/router';
import { UserSession } from '../shared/classes/user-session';
import { Agence } from '../shared/classes/agence';
import { Bureau } from '../shared/classes/bureau';
import { ApiResponse } from '../shared/classes/api';
import { StorageService } from '../../shared/services/storage.service';
import { Batiment } from '../../infrastructure/shared/classes/batiment';
import { Role } from '../../identite/shared/classes/role';
import { ConfigurationClient } from '../shared/classes/configuration-client';
import { Mode } from '../shared/enum/mode';
import { TauxTvaService } from './taux-tva.service';

@Injectable()
export class AuthenticationService {

    private timer: Observable<number>;
    private timerSubscription: Subscription;
    public timeoutExpired: Subject<number> = new Subject<number>();
    public timerEvent: EventEmitter<any>;
    public bureauEvent: EventEmitter<any>;
    public maitreEvent: EventEmitter<any>;
    public batimentEvent: EventEmitter<any>;
    public logoutEvent: EventEmitter<any>;
    _timeoutSeconds: number;
    sessionExist = false;
    _count: any;
    resetOnTrigger: any;

    constructor(
        private tauxTvaService: TauxTvaService,
        private apiService: ApiService,
        private router: Router,
        private storageService: StorageService,
    ) {
        this.timerEvent     = new EventEmitter();
        this.bureauEvent    = new EventEmitter();
        this.maitreEvent  = new EventEmitter();
        this.batimentEvent  = new EventEmitter();
        this.logoutEvent    = new EventEmitter();
        this.timeoutExpired.subscribe(n => {
            this.router.navigate(['/login']);
        });
        this.tauxTvaService.onTauxNormalTva();
    }

    login(username: string, password: string): Observable<ApiResponse> {
        return this.apiService.post(`authentification`, { login: username, password: password, soft: 'a17' })
        .pipe(map(retour => {
            // login successful if there's a jwt token in the response
            if (retour.data && retour.data.authentication) {

                // store user details and jwt token in local storage to keep user logged in between page refreshes
                const user = new UserSession(retour.data.session_storage.user);
                user.token = retour.data.token;
                localStorage.setItem('currentUser', JSON.stringify(user));

                const agence = new Agence(retour.data.session_storage.agence);
                localStorage.setItem('agence', JSON.stringify(agence));

                let bureau = null;
                if (retour.data.session_storage.bureau.id !== undefined) {
                    bureau = new Bureau(retour.data.session_storage.bureau);
                }
                localStorage.setItem('bureau', JSON.stringify(bureau));

                if (retour.data.acl_access) {
                    localStorage.setItem('acl', JSON.stringify(retour.data.acl_access));
                    this.storageService.setAcl(retour.data.acl_access);
                }

                if (retour.data.configuration) {
                    localStorage.setItem('configuration', JSON.stringify(retour.data.configuration));
                    this.storageService.setConfiguration(retour.data.configuration);
                }

                if (retour.data.configuration_client) {
                    localStorage.setItem('configuration_client', JSON.stringify(retour.data.configuration_client));
                    this.storageService.configurationClient = new ConfigurationClient(retour.data.configuration_client);
                }

                this.tauxTvaService.onTauxNormalTva();
            }

            return retour;

        }));
    }

    logout() {
        // remove user from local storage to log user out
        this.sessionExist = false;
        localStorage.removeItem('currentUser');
        localStorage.removeItem('bureau');
        localStorage.removeItem('agence');
        localStorage.removeItem('acl');
        localStorage.removeItem('optimaxUser');
        localStorage.removeItem('optimaxToken');
        localStorage.removeItem('optimaxHasApi');
        localStorage.removeItem('optimaxApiType');
        localStorage.removeItem('optimaxComposeUrl');
    }


    checkExpiration() {
        if (!this.sessionExist) {
            const user = JSON.parse(localStorage.getItem('currentUser'));
            if (user && user.token) {
                return this.apiService.get(`session-expiration/` + user.token)
                .subscribe(retour => {
                    if (retour.status === 200 && retour.data.expiration && parseInt(retour.data.expiration, 10) > 0) {
                        this.sessionExist = true;
                        const now = new Date().getTime();
                        this._timeoutSeconds = retour.data.expiration - Math.floor(now / 1000);
                        this.startTimer();
                    } else {
                        this.router.navigate(['/login']);
                    }
                });
            }
        }
    }


    setBureau(bureau: Bureau): Observable<ApiResponse> {
        localStorage.setItem('bureau', JSON.stringify(bureau));
        this.storageService.clearCache();
        return this.apiService.put('session/bureau/' + bureau.id)
        .pipe(
            map(retour => {
                this.bureauEvent.emit(bureau);
                return retour;
            })
        );
    }


    setMaitre(maitre: Role) {
        localStorage.setItem('maitreOuvrage', JSON.stringify(maitre));
        this.maitreEvent.emit(maitre);
    }

    resetMaitre() {
        localStorage.setItem('maitreOuvrage', JSON.stringify(new Role({id: '0'})));
        this.maitreEvent.emit(new Role({id: '0'}));
    }

    setBatiment(batiment: Batiment) {
        localStorage.setItem('batiment', JSON.stringify(batiment));
        this.batimentEvent.emit(batiment);
    }

    resetBatiment() {
        localStorage.setItem('batiment', JSON.stringify(new Batiment({id: '0'})));
        this.batimentEvent.emit(new Batiment({id: '0'}));
    }

    startTimer() {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
        }

        this.timer = timer(this._timeoutSeconds * 1000);
        this.timerSubscription = this.timer.subscribe(n => {
            this.timerComplete(n);
        });

        this.timerEvent.emit(this._timeoutSeconds);
    }


    private timerComplete(n: number) {
        this.timeoutExpired.next(++this._count);

        if (this.resetOnTrigger) {
            this.startTimer();
        }
    }
    loginMicrosoft(username: string, password: string): Observable<ApiResponse> {
        return this.apiService.post(`authentification/microsoft`, { login: username, password: password, soft: 'a17' })
        .pipe(map(retour => {
            // login successful if there's a jwt token in the response
            if (retour.data && retour.data.authentication) {
                return this.onLogin(retour);
            }
        }));
    }
    private onLogin(retour) {
        const { data } = retour;
        // login successful if there's a jwt token in the response
        if (data && data.authentication) {
            const { session_storage } = data;
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            const user = new UserSession(session_storage.user);
            user.token = data.token;
            localStorage.setItem('currentUser', JSON.stringify(user));

            const agence = new Agence(session_storage.agence);
            localStorage.setItem('agence', JSON.stringify(agence));

            let bureau = null;
            if (session_storage.bureau.id !== undefined) {
                bureau = new Bureau(session_storage.bureau);
            }
            localStorage.setItem('bureau', JSON.stringify(bureau));

            if (data.acl_access) {
                localStorage.setItem('acl', JSON.stringify(data.acl_access));
                this.storageService.setAcl(data.acl_access);
            }

            if (data.configuration) {
                localStorage.setItem('configuration', JSON.stringify(data.configuration));
                this.storageService.setConfiguration(data.configuration);
            }

            if (data.configuration_client) {
                localStorage.setItem('configuration_client', JSON.stringify(data.configuration_client));
                this.storageService.configurationClient = new ConfigurationClient(data.configuration_client);
            }
        }

        return retour;
    }
    getSSOActivated() {
        return this.apiService.get('sso');
    }

    demandePasswordChange(data) : Observable<ApiResponse> {
        return this.apiService.post('demande-password-change', data);
    }

    changePassword(data) : Observable<ApiResponse> {
        return this.apiService.post('change-password', data);
    }

    verifyOneTimeToken(data): Observable<ApiResponse> {
        return this.apiService.post('verify-token', data);
    }

    getDisconnectedMailsList(roleId) : Observable<ApiResponse> {
        return this.apiService.get(`optimax/disconnected-mails-list/${roleId}`)
    }

    decryptOneTimeToken(data): Observable<ApiResponse> {
        return this.apiService.post('decrypt-token', data);
    }

    validationFactureFournisseur(data): Observable<ApiResponse> {
        return this.apiService.post('validation-facture-fournisseur', data);
    }
    
}
